import { useEffect } from "react";
import { shallowEqual } from "react-redux";
import { addVisitedVehicleId, getVisitedVehicleIds, uscLocalStorageParamSelector } from "../utils/localStorage";
import {
    calculateExternalPrice,
    setExternalPriceShow,
    setUserTaxLocation,
} from "../redux/actions/UscExternalPriceActions";
import { externalPriceFnIsAvailable, initializeExternalPriceFn, showExternalPrice } from "../utils/externalPriceUtils";
import { getSessionStorageParams, getTaxLocationIdFromStorage } from "../utils/sessionStorage";
import { getVehiclePriceSource } from "../utils/priceUtils";
import { showUscPrice } from "../../shared-logic/utils/uscPriceUtils";
import { enableUscMonthlyRate } from "../utils/settings";
import { startFinanceFetch } from "../redux/actions/UscFinanceActions";
import { type UscDispatchType, useUscSelector } from "../redux/store";
import { useDispatch } from "../../../shared-logic/redux/store";

const useUscExternalPriceInit = (isPdp: boolean): void => {
    const dispatch = useDispatch<UscDispatchType>();
    const localStorageParams = useUscSelector(uscLocalStorageParamSelector, shallowEqual);
    const sessionStorageParams = useUscSelector(getSessionStorageParams, shallowEqual);
    const vehicle = useUscSelector((state) => state.vehicle);
    const uscSettings = useUscSelector((state) => state.uscSettings);
    const commonSettings = useUscSelector((state) => state.commonSettings);
    const userTaxLocationId = useUscSelector((state) => state.externalPrice.userTaxLocationId);
    // Determine if the dealer fetch flow is done. This is necessary as dealer fetch can impact the external price.
    const dealerInitialized = useUscSelector(
        ({ vehicle: dealerVehicle, dealer }) =>
            (dealerVehicle.result && !dealerVehicle.result.dealerId) || dealer.initialized,
    );

    const { vehicleForSaleId, result } = vehicle;
    const { enableExternalPrice } = uscSettings;
    const vehicleLoaded = !!result;

    const showPrice = showUscPrice(getVehiclePriceSource(vehicle.result));
    // Client-side context related logic.
    // Determine if we need to show the external price and flag the vehicle as visited if required.
    useEffect(() => {
        if (enableExternalPrice && showPrice) {
            // SessionStorage will contain the taxLocationId if it has been manually set by the user.
            // If we don't already have a location ID (query string/tyCode/...) use that one.
            const sessionTaxLocationId = getTaxLocationIdFromStorage(sessionStorageParams);
            if (sessionTaxLocationId && !userTaxLocationId) dispatch(setUserTaxLocation(sessionTaxLocationId));

            // Note that even though it is possible to 'hide' the external price with the setExternalPriceShow action, if it enabled with the enableExternalPrice setting we still keep calculating the value.
            // This is done mostly for consistency but it also benefits performance once we have a full SPA approach for AEM versions.
            dispatch(setExternalPriceShow(showExternalPrice(uscSettings, localStorageParams, vehicleForSaleId, isPdp)));

            // Because the PDP behaves as an SPA the cash price will update once the external prices are shown after moving to a different step (OR-7891)
            // Therefor we will need to recalculate the finance prices once we show the external prices.
            // We only recalculate these prices when the external price is already available
            if (
                externalPriceFnIsAvailable() &&
                enableUscMonthlyRate(commonSettings, uscSettings, vehicle.result, vehicle.type)
            ) {
                dispatch(startFinanceFetch());
            }

            // Step is not PDP, save vehicle as visited.
            if (!isPdp && !getVisitedVehicleIds(localStorageParams).includes(vehicleForSaleId)) {
                addVisitedVehicleId(localStorageParams, vehicleForSaleId);
            }
        }
    }, [isPdp]);

    // Initialize the frontend external price calculation when required.
    useEffect(() => {
        if (enableExternalPrice && showPrice && vehicleLoaded && dealerInitialized) {
            const initExternalPrice = async (): Promise<void> => {
                const { success } = await initializeExternalPriceFn(commonSettings, uscSettings);
                if (success) dispatch(calculateExternalPrice());
            };
            initExternalPrice();
        }
    }, [vehicleLoaded, dealerInitialized]);
};

export default useUscExternalPriceInit;
